import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Upload } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import './styles/project-cover-field.css';
/** 封面最大体积（MB）。 */
const COVER_MAX_SIZE_MB = 5;
/** 允许上传的封面图片 MIME 类型。 */
const COVER_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/**
 * 读取本地封面文件为 dataURL，选中后仅用于本地预览与提交。
 * @param file 封面文件。
 * @returns dataURL 字符串。
 */
function readCoverAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = () => {
            reject(new Error('封面文件读取失败'));
        };
        reader.readAsDataURL(file);
    });
}
/**
 * 渲染通用封面字段：点击封面（或占位区）即重新选择本地图片，选中后只做本地预览，
 * 真正的上传在提交表单时统一经上传素材接口完成；悬停展示删除按钮，删除即清空封面。
 * 由项目创建/编辑与打包发布共用，字段值形态为 dataURL 或已保存的 OSS URL。
 */
export function ProjectCoverField(props) {
    /** 当前封面地址，无封面时用空字符串参与判断。 */
    const coverSrc = props.value ?? '';
    /**
     * 校验封面格式与体积，仅做本地拦截。
     * @param file 待校验的封面文件。
     * @returns 是否通过校验。
     */
    function isCoverValid(file) {
        if (!COVER_ALLOWED_TYPES.includes(file.type)) {
            props.onError?.('封面仅支持 JPG / PNG / WebP 格式图片');
            return false;
        }
        if (file.size / 1024 / 1024 > COVER_MAX_SIZE_MB) {
            props.onError?.(`封面图片大小不能超过 ${String(COVER_MAX_SIZE_MB)}MB`);
            return false;
        }
        return true;
    }
    /**
     * 选中本地封面：读取为 dataURL 作为表单值与预览，此时不发起任何上传请求。
     * @param file 选中的封面文件。
     * @returns 始终返回 false，阻止 antd 自身的上传行为。
     */
    async function handleBeforeUpload(file) {
        if (!isCoverValid(file)) {
            return false;
        }
        try {
            props.onChange?.(await readCoverAsDataUrl(file));
        }
        catch {
            props.onError?.('封面读取失败，请重新选择');
        }
        return false;
    }
    /** 删除封面：清空表单值即可，OSS 上的旧封面由保存链路按旧值清理。 */
    function handleRemove() {
        props.onChange?.(undefined);
    }
    return (_jsxs("div", { className: "project-cover-field", children: [_jsx(Upload, { className: "project-cover-field__upload", id: props.id, "aria-required": props['aria-required'], accept: COVER_ALLOWED_TYPES.join(','), listType: "picture-card", showUploadList: false, beforeUpload: handleBeforeUpload, children: coverSrc ? (_jsxs("span", { className: "project-cover-field__preview", children: [_jsx("img", { className: "project-cover-field__image", src: coverSrc, alt: "\u5C01\u9762\u9884\u89C8" }), _jsx("span", { className: "project-cover-field__mask", "aria-hidden": "true", children: "\u70B9\u51FB\u66F4\u6362" })] })) : (_jsxs("span", { className: "project-cover-field__trigger", children: [_jsx(Plus, { "aria-hidden": "true" }), _jsx("span", { children: "\u4E0A\u4F20\u5C01\u9762" })] })) }), coverSrc ? (_jsx(Button, { className: "project-cover-field__remove", type: "text", icon: _jsx(Trash2, { "aria-hidden": "true", size: 16 }), "aria-label": "\u5220\u9664\u5C01\u9762", onClick: handleRemove })) : null] }));
}
